import { Component, ElementRef, EventEmitter, Input, OnChanges, Output, SimpleChanges, ViewChild, computed, signal } from '@angular/core';
import { catchError, forkJoin, Observable, of } from 'rxjs';
import { SyncService } from '../../../services/sync-service';
import { FieldContext, TapDetail } from '../../../models/field-context';
import { FieldIrrigationRate, FieldLinkAuditEntry, FmsBlockCandidate, IrrigationRateHistoryEntry, TapCandidate } from '../../../models/field-link';
import { DropdownOption } from '../../../ui-kit/dropdown/dropdown.component';
import {
  blockKey, buildMoveRows, filterFmsBlocks, filterTaps, fmsFarmName, FmsStatusFilter, groupTapsByFarm,
  isOnOtherField, otherFieldCount, tapFarm, TapStatusFilter
} from './field-link-filters';
import { describeRateChange, mmFor, mmLabel, parseRateText, percentChangeLabel, rateInputText, rateLabel } from './irrigation-rate';

const NO_BLOCK = 'NONE';
/** The run length the rate tab uses for its worked example. */
const EXAMPLE_MINUTES = 45;

export type LinkTab = 'fms' | 'igator' | 'rate';
type RateSource = FieldIrrigationRate['source'];

/**
 * Sets a field's FMS block and iGator taps by hand. Saving marks the field as manually linked, after
 * which the sync service's matchers leave it alone. Taps picked from another field are moved, so the
 * user confirms those first, with the field each one is leaving and what that field keeps.
 *
 * The rate tab sets how iGator run time becomes mm: the field's own rate, or its farm's. A farm change
 * reaches every field on the farm rate, past irrigation included, so that is confirmed first too.
 */
@Component({
  selector: 'app-field-link-editor',
  templateUrl: './field-link-editor.component.html',
  standalone: false
})
export class FieldLinkEditorComponent implements OnChanges {
  @Input() open = false;
  @Input() fieldId: string;
  @Input() fieldName = '';
  @Input() context: FieldContext | null = null;
  /** The tab the modal opens on. */
  @Input() initialTab: LinkTab = 'fms';
  /** Emits true when something was saved, so the caller reloads the field context. */
  @Output() closed = new EventEmitter<boolean>();

  readonly NO_BLOCK = NO_BLOCK;
  readonly loading = signal(false);
  readonly saving = signal(false);
  readonly error = signal<string | null>(null);
  readonly step = signal<'edit' | 'confirm'>('edit');
  readonly tab = signal<LinkTab>('fms');

  readonly fmsCandidates = signal<FmsBlockCandidate[]>([]);
  readonly fmsValue = signal<string>(NO_BLOCK);
  private originalFms = NO_BLOCK;
  readonly fmsSearch = signal('');
  readonly fmsStatus = signal<FmsStatusFilter>('all');
  readonly fmsFarm = signal<string | null>(null);

  readonly tapCandidates = signal<TapCandidate[]>([]);
  readonly selectedTaps = signal<Set<string>>(new Set());
  private originalTaps = new Set<string>();
  readonly tapSearch = signal('');
  readonly tapStatus = signal<TapStatusFilter>('all');
  readonly tapFarmFilter = signal<string | null>(null);

  readonly history = signal<FieldLinkAuditEntry[]>([]);

  /** Null until loaded, and stays null when the rate could not be loaded; the link tabs still work. */
  readonly rate = signal<FieldIrrigationRate | null>(null);
  readonly rateUnavailable = signal(false);
  readonly fieldRateMode = signal<'farm' | 'field'>('farm');
  readonly fieldRateText = signal('');
  readonly farmRateText = signal('');
  private originalFieldRateMode: 'farm' | 'field' = 'farm';
  private originalFieldRateText = '';
  private originalFarmRateText = '';

  readonly rateLabel = rateLabel;
  readonly mmLabel = mmLabel;
  readonly describeRateChange = describeRateChange;
  readonly EXAMPLE_MINUTES = EXAMPLE_MINUTES;

  /** The field's own rate as it will be sent: null means "use the farm rate". */
  readonly fieldRateValue = computed(() => this.fieldRateMode() === 'farm' ? null : parseRateText(this.fieldRateText()));
  readonly farmRateValue = computed(() => parseRateText(this.farmRateText()));
  readonly fieldRateChanged = computed(() => this.rate() !== null && (this.fieldRateMode() !== this.originalFieldRateMode
    || (this.fieldRateMode() === 'field' && this.fieldRateText().trim() !== this.originalFieldRateText)));
  readonly farmRateChanged = computed(() => this.rate()?.farmId != null && this.farmRateText().trim() !== this.originalFarmRateText);
  readonly rateChanged = computed(() => this.fieldRateChanged() || this.farmRateChanged());

  /** Why the rate tab cannot be saved as it stands, or null. */
  readonly rateError = computed<string | null>(() => {
    if (this.fieldRateChanged()) {
      const own = this.fieldRateValue();
      if (own === null && this.fieldRateMode() === 'field') return 'Enter a rate for this field, or choose "Use the farm rate".';
      if (own === 'invalid') return 'The field rate must be a number above 0 and at most 100 mm/h.';
    }
    if (this.farmRateChanged() && this.farmRateValue() === 'invalid') return 'The farm rate must be a number above 0 and at most 100 mm/h.';
    return null;
  });

  /** The rate this field will use once saved, and where it comes from. Unsaved valid edits count. */
  readonly rateInUse = computed<{ mmPerHour: number; source: RateSource } | null>(() => {
    const r = this.rate();
    if (!r) return null;
    const own = this.fieldRateValue();
    if (typeof own === 'number') return { mmPerHour: own, source: 'FIELD' };
    if (this.fieldRateMode() === 'field') return { mmPerHour: r.effectiveMmPerHour, source: r.source };
    const farm = this.farmRateChanged() ? this.farmRateValue() : r.farmMmPerHour;
    if (typeof farm === 'number') return { mmPerHour: farm, source: 'FARM' };
    return { mmPerHour: r.defaultMmPerHour, source: 'DEFAULT' };
  });
  readonly exampleMm = computed(() => {
    const inUse = this.rateInUse();
    return inUse ? mmFor(EXAMPLE_MINUTES, inUse.mmPerHour) : null;
  });

  /** What a farm rate change does, for the confirm step. Null when the farm rate is not changing. */
  readonly farmChange = computed(() => {
    const r = this.rate();
    const next = this.farmRateValue();
    if (!r || !this.farmRateChanged() || next === 'invalid') return null;
    const from = r.farmMmPerHour ?? r.defaultMmPerHour;
    const to = next ?? r.defaultMmPerHour;
    // Fields that follow the farm rate after this save. This field moves between the two groups if its own rate is set or cleared now.
    const hadOwn = r.fieldMmPerHour != null;
    const willOwn = typeof this.fieldRateValue() === 'number';
    const shift = hadOwn && !willOwn ? 1 : !hadOwn && willOwn ? -1 : 0;
    return {
      farmName: r.farmName ?? 'this farm',
      from, to, cleared: next === null,
      onFarmRate: r.farmFieldCount - r.farmOverrideCount + shift,
      ownRate: r.farmOverrideCount - shift,
      change: percentChangeLabel(from, to),
      exampleFrom: mmFor(EXAMPLE_MINUTES, from),
      exampleTo: mmFor(EXAMPLE_MINUTES, to)
    };
  });

  readonly rateHistory = computed<IrrigationRateHistoryEntry[]>(() => this.rate()?.history ?? []);
  readonly historyCount = computed(() => this.history().length + this.rateHistory().length);

  readonly currentBlock = computed(() => this.fmsCandidates().find(b => blockKey(b) === this.fmsValue()) ?? null);
  /** Farm filter options for the shared ui-dropdown; '' is "All farms". */
  readonly fmsFarmOptions = computed<DropdownOption[]>(() =>
    farmOptions([...new Set(this.fmsCandidates().map(fmsFarmName))].sort()));
  readonly visibleBlocks = computed(() =>
    filterFmsBlocks(this.fmsCandidates(), this.originalFms, { search: this.fmsSearch(), status: this.fmsStatus(), farm: this.fmsFarm() }));

  /** What will be on this field after saving, in name order. */
  readonly linkedTaps = computed(() => [...this.selectedTaps()].sort());
  readonly tapFarmOptions = computed<DropdownOption[]>(() => farmOptions(groupTapsByFarm(this.tapCandidates()).map(g => g.farm)));
  readonly availableTapGroups = computed(() => groupTapsByFarm(filterTaps(this.tapCandidates(), this.fieldId, this.selectedTaps(), {
    search: this.tapSearch(), status: this.tapStatus(), farm: this.tapFarmFilter()
  })));

  /** Selected taps that currently sit on a different field and will be taken from it. */
  readonly moveRows = computed(() => buildMoveRows(this.tapCandidates(), this.fieldId, this.selectedTaps()));
  readonly tapsToMove = computed(() => this.tapCandidates().filter(c => this.selectedTaps().has(c.tap) && this.isOnOtherField(c)));

  /** The open tab's saved link, when it was set by hand: shown in the header. */
  readonly manualChange = computed(() => {
    if (this.tab() === 'rate') return null;
    const link = this.tab() === 'fms' ? this.context?.fms : this.context?.igator;
    return link?.manual ? link.lastManualChange : null;
  });

  readonly fmsChanged = computed(() => this.fmsValue() !== this.originalFms);
  readonly tapsChanged = computed(() => !sameSet(this.selectedTaps(), this.originalTaps));

  /** The title: focus moves there on opening, so the keyboard is in the modal rather than behind it. */
  @ViewChild('editTitle') private editTitle?: ElementRef<HTMLElement>;
  /** What had focus when the editor opened (a link row, or a button in the integration drawer). */
  private returnFocusTo: HTMLElement | null = null;

  constructor(private syncService: SyncService) { }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['open'] && this.open) {
      this.reset();
      this.load();
      this.returnFocusTo = document.activeElement instanceof HTMLElement ? document.activeElement : null;
      // Once the modal has rendered.
      setTimeout(() => this.editTitle?.nativeElement.focus());
    } else if (changes['open'] && !changes['open'].firstChange && !this.open) {
      const target = this.returnFocusTo;
      this.returnFocusTo = null;
      // Once the integration drawer, inert under the editor, is reachable again.
      setTimeout(() => target?.focus());
    }
  }

  isOnOtherField(c: TapCandidate): boolean {
    return isOnOtherField(c, this.fieldId);
  }

  otherFields(b: FmsBlockCandidate): number {
    return otherFieldCount(b, this.originalFms);
  }

  farmOf(b: FmsBlockCandidate): string {
    return fmsFarmName(b);
  }

  blockKeyOf(b: FmsBlockCandidate): string {
    return blockKey(b);
  }

  tapFarmOf(tap: string): string {
    return tapFarm(tap);
  }

  /** How a tap on the left came to be there: already linked (with how), or added in this edit. */
  tapOrigin(tap: string): { kind: 'new' } | { kind: 'existing'; detail: TapDetail | null } {
    if (!this.originalTaps.has(tap)) return { kind: 'new' };
    return { kind: 'existing', detail: this.context?.igator?.tapDetails?.find(d => d.tap === tap) ?? null };
  }

  fieldLabel(c: { fieldId: number | null; fieldName: string | null }): string {
    return c.fieldName || 'field ' + c.fieldId;
  }

  candidateFor(tap: string): TapCandidate | null {
    return this.tapCandidates().find(c => c.tap === tap) ?? null;
  }

  toggleTap(tap: string): void {
    const next = new Set(this.selectedTaps());
    next.has(tap) ? next.delete(tap) : next.add(tap);
    this.selectedTaps.set(next);
  }

  /** Taps coming from another field, or a farm rate change: either is confirmed before saving. */
  readonly needsConfirm = computed(() => (this.tapsChanged() && this.tapsToMove().length > 0) || this.farmChange() !== null);
  readonly confirmLabel = computed(() =>
    this.tapsToMove().length > 0 && this.tapsChanged() ? 'Move and save' : this.farmChange() ? 'Change farm rate' : 'Save');

  save(): void {
    if (!this.fmsChanged() && !this.tapsChanged() && !this.rateChanged()) {
      this.closed.emit(false);
      return;
    }
    const rateError = this.rateError();
    if (rateError) {
      this.step.set('edit');
      this.tab.set('rate');
      this.error.set(rateError);
      return;
    }
    if (this.step() === 'edit' && this.needsConfirm()) {
      this.step.set('confirm');
      return;
    }
    const calls: Observable<unknown>[] = [];
    if (this.fmsChanged()) {
      const value = this.fmsValue();
      const [blockNo, farmId] = value.split('|');
      calls.push(this.syncService.setFmsLink(this.fieldId, value === NO_BLOCK ? null : { blockNo, fmsFarmId: Number(farmId) }));
    }
    if (this.tapsChanged()) {
      calls.push(this.syncService.setIgatorLinks(this.fieldId, [...this.selectedTaps()]));
    }
    if (this.fieldRateChanged()) {
      calls.push(this.syncService.setFieldIrrigationRate(this.fieldId, this.fieldRateValue() as number | null));
    }
    if (this.farmRateChanged()) {
      calls.push(this.syncService.setFarmIrrigationRate(this.rate()!.farmId!, this.farmRateValue() as number | null));
    }
    this.saving.set(true);
    this.error.set(null);
    forkJoin(calls).subscribe({
      next: () => {
        this.saving.set(false);
        this.closed.emit(true);
      },
      error: () => {
        this.saving.set(false);
        this.step.set('edit');
        // Links and rates save separately, so one may have gone through. Say so rather than claim nothing changed.
        this.error.set('Saving failed. Part of the change may have been saved: close and reopen to see the current links and rates.');
      }
    });
  }

  cancel(): void {
    if (this.step() === 'confirm') {
      this.step.set('edit');
      return;
    }
    this.closed.emit(false);
  }

  private reset(): void {
    const fms = this.context?.fms;
    this.originalFms = fms?.linked && fms.blockNo && fms.fmsFarmId != null ? `${fms.blockNo}|${fms.fmsFarmId}` : NO_BLOCK;
    this.fmsValue.set(this.originalFms);
    this.originalTaps = new Set(this.context?.igator?.taps ?? []);
    this.selectedTaps.set(new Set(this.originalTaps));
    this.tab.set(this.initialTab);
    this.fmsSearch.set('');
    this.fmsStatus.set('all');
    this.fmsFarm.set(null);
    this.tapSearch.set('');
    this.tapStatus.set('all');
    this.tapFarmFilter.set(null);
    this.step.set('edit');
    this.error.set(null);
    this.setRate(null);
    this.rateUnavailable.set(false);
  }

  /** Puts the loaded rate in the form, as the starting point the "changed" checks compare with. */
  private setRate(rate: FieldIrrigationRate | null): void {
    this.rate.set(rate);
    this.originalFieldRateMode = rate?.fieldMmPerHour != null ? 'field' : 'farm';
    this.originalFieldRateText = rateInputText(rate?.fieldMmPerHour);
    this.originalFarmRateText = rateInputText(rate?.farmMmPerHour);
    this.fieldRateMode.set(this.originalFieldRateMode);
    this.fieldRateText.set(this.originalFieldRateText);
    this.farmRateText.set(this.originalFarmRateText);
  }

  private load(): void {
    this.loading.set(true);
    forkJoin({
      fms: this.syncService.getFmsLinkCandidates(),
      taps: this.syncService.getIgatorLinkCandidates(),
      history: this.syncService.getFieldLinkAudit(this.fieldId),
      // Loaded on its own terms: a sync service without the rate endpoint must not take the link tabs down.
      rate: this.syncService.getFieldIrrigationRate(this.fieldId).pipe(catchError(() => of(null)))
    }).subscribe({
      next: ({ fms, taps, history, rate }) => {
        this.fmsCandidates.set(fms);
        this.tapCandidates.set(taps);
        this.history.set(history);
        this.setRate(rate);
        this.rateUnavailable.set(rate === null);
        this.loading.set(false);
      },
      error: () => {
        this.loading.set(false);
        this.error.set('Could not load the FMS blocks and iGator taps to choose from.');
      }
    });
  }
}

function farmOptions(farms: string[]): DropdownOption[] {
  return [{ id: '', name: 'All farms' }, ...farms.map(f => ({ id: f, name: f }))];
}

function sameSet(a: Set<string>, b: Set<string>): boolean {
  return a.size === b.size && [...a].every(x => b.has(x));
}
